import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { MemberDiscountGrantSchema } from '../discount-grant/discount-grant';

/** How one question went, on the result screen only — never during the attempt. */
export const TestAttemptQuestionResultSchema = z
  .object({
    questionId: UuidSchema,
    isCorrect: z.boolean(),
    /** The answer key, released now that the attempt is scored and closed. */
    correctOptionIds: z.array(UuidSchema),
    chosenOptionIds: z.array(UuidSchema),
    explanation: z.string().nullable(),
  })
  .strict();

export type TestAttemptQuestionResult = z.infer<typeof TestAttemptQuestionResultSchema>;

/**
 * What a member sees after submitting (matrix rows 3–5, 9).
 *
 * ## The grants are here, and that is why scoring is synchronous
 *
 * A member who has just passed is told what they earned on the screen that told
 * them they passed. Issuing the grants in a background job would mean a results
 * screen that says "your discount is being calculated", and a member reloading a
 * list to find out whether the platform kept its promise.
 *
 * `grants` is **empty on a retake after a pass** (row 9) and empty when no rule
 * matched — the client says "no new discount" from an empty array rather than
 * from a flag, so the two cases read the same to a member, which they should.
 */
export const TestAttemptResultSchema = z
  .object({
    attemptId: UuidSchema,
    testId: UuidSchema,
    passed: z.boolean(),
    scorePct: z.number().int().min(0).max(100),
    passingScorePct: z.number().int().min(1).max(100),
    correctCount: z.number().int().nonnegative(),
    questionCount: z.number().int().nonnegative(),
    /** How many takes remain after this one. `null` when unlimited. */
    attemptsRemaining: z.number().int().nonnegative().nullable(),
    /** When another take becomes possible — `null` when none is permitted or needed. */
    retakeAvailableAt: z.iso.datetime().nullable(),
    questionResults: z.array(TestAttemptQuestionResultSchema),
    /** Grants this submission earned — empty is the ordinary case. */
    grants: z.array(MemberDiscountGrantSchema),
  })
  .strict();

export type TestAttemptResult = z.infer<typeof TestAttemptResultSchema>;
